<script setup>
import { RouterLink } from "vue-router";
import GlDisclosureDropdown from "~/components/gitlab/GlDisclosureDropdown.vue";
import GlIcon from "~/components/gitlab/GlIcon.vue";
import { runLabel } from "~/utils/runLabel";

const STATUS_ICONS = {
  failed: { color: "var(--gl-status-danger-icon-color)", name: "status-failed" },
  running: { color: "var(--gl-status-info-icon-color)", name: "status-running" },
  success: { color: "var(--gl-status-success-icon-color)", name: "status-success" },
  warning: { color: "var(--gl-status-warning-icon-color)", name: "status-alert" }
};

defineProps({
  jobId: { required: true, type: [String, Number] },
  projectId: { required: true, type: [String, Number] },
  run: { required: true, type: Object }
});

function statusIcon(status) {
  return STATUS_ICONS[status] ?? STATUS_ICONS.running;
}

function mergeRequestDropdownItems(mergeRequests) {
  return [
    {
      items: mergeRequests.map((mr) => ({ text: `#${mr.iid}`, href: mr.webUrl })),
      name: "Affected merge requests"
    }
  ];
}
</script>

<template>
  <li
    class="flex items-center gap-4 border-b border-[var(--gl-border-color-default)] px-5 py-4 last:border-b-0"
    data-testid="run-entry"
  >
    <!-- Status icon -->
    <GlIcon
      :name="statusIcon(run.status).name"
      :size="16"
      :style="{ color: statusIcon(run.status).color }"
      class="shrink-0"
    />

    <!-- Run info -->
    <div class="min-w-0 flex-1">
      <h3 class="text-sm font-semibold leading-snug text-[var(--gl-text-color-default)]">
        {{ runLabel(run.name) }} #{{ run.id }}
      </h3>
      <p class="text-xs text-[var(--gl-text-color-subtle)]">
        <!-- Affected merge requests -->
        <template v-if="run.mergeRequests.length > 0"
          >Affected
          <a
            :href="run.mergeRequests[0].webUrl"
            target="_blank"
            rel="noopener noreferrer"
            class="text-[var(--gl-text-color-link)] hover:underline"
            >#{{ run.mergeRequests[0].iid }}</a
          >
          <template v-if="run.mergeRequests.length > 1">
            and {{ run.mergeRequests.length - 1 }} more
            <GlDisclosureDropdown
              :items="mergeRequestDropdownItems(run.mergeRequests)"
              icon="ellipsis_h"
              no-caret
              category="tertiary"
              toggle-text="Show affected merge requests"
              text-sr-only
              fluid-width
              class="align-middle"
            />
          </template>
        </template>

        <!-- Separator + time -->
        <template v-if="run.mergeRequests.length > 0 && (run.createdAt || run.runtime)">
          <span class="mx-2 text-[var(--gl-text-color-disabled)]">|</span>
        </template>
        <span v-if="run.createdAt"
          >triggered {{ run.createdAt }}<template v-if="run.runtime"> and ran for {{ run.runtime }}</template></span
        >

        <!-- Separator + view logs link -->
        <span class="mx-2 text-[var(--gl-text-color-disabled)]">|</span>
        <RouterLink
          :to="{ name: 'run', params: { projectId, jobId, runId: run.id } }"
          class="text-[var(--gl-text-color-link)] hover:underline"
          >view logs</RouterLink
        >
      </p>
    </div>
  </li>
</template>
